import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/src/context/ThemeContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import { useAuth } from '@/src/context/AuthContext';
import { PaywallScreen } from '@/src/presentation/screens/PaywallScreen';
import { getOfferings, purchasePackage, restorePurchases } from '@/src/core/config/revenuecat';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { useTranslation } from '@/src/i18n';
import { logger } from '@/src/core/utils';

export default function PaywallContainer() {
  const { colors } = useTheme();
  const { isPro, refreshSubscription } = useSubscription();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [loading, setLoading] = useState(false);
  const [monthlyPrice, setMonthlyPrice] = useState<string | undefined>();
  const [yearlyPrice, setYearlyPrice] = useState<string | undefined>();

  const extractPrice = (pkg: any): string | undefined =>
    pkg?.rcBillingProduct?.currentPrice?.formattedPrice ??
    pkg?.product?.priceString;

  useEffect(() => {
    getOfferings().then((offerings) => {
      setMonthlyPrice(extractPrice(offerings?.current?.monthly));
      setYearlyPrice(extractPrice(offerings?.current?.annual));
    });
  }, []);

  const handleSubscribe = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const offerings = await getOfferings();



      if (!offerings?.current) {
        platformAlert(
          t('subscription.error'),
          t('subscription.noOfferings'),
          [{ text: t('common.ok') }]
        );
        return;
      }

      const pkg = selectedPlan === 'yearly'
        ? offerings.current.annual
        : offerings.current.monthly;

      if (!pkg) {
        platformAlert(
          t('subscription.error'),
          t('subscription.noOfferings'),
          [{ text: t('common.ok') }]
        );
        return;
      }

      const success = await purchasePackage(pkg);

      if (success) {
        await refreshSubscription();
        platformAlert(
          t('subscription.success'),
          t('subscription.welcomePro'),
          [{ text: t('common.ok') }]
        );
      }
    } catch (error) {
      logger.error('Paywall', 'Purchase failed', { error });
      platformAlert(
        t('subscription.error'),
        t('subscription.purchaseFailed'),
        [{ text: t('common.ok') }]
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    setLoading(true);

    try {
      // On web, subscriptions are managed via the payment provider (RevenueCat Web Billing).
      // Just refresh from Supabase which is the source of truth.
      if (Platform.OS === 'web') {
        await refreshSubscription();
        const message = isPro
          ? t('subscription.restoreSuccess')
          : t('subscription.noSubscriptionFound');
        platformAlert(t('subscription.restorePurchases'), message, [{ text: t('common.ok') }]);
        return;
      }

      const hasPro = await restorePurchases();

      if (hasPro) {
        await refreshSubscription();
        platformAlert(
          t('subscription.success'),
          t('subscription.restoreSuccess'),
          [{ text: t('common.ok') }]
        );
      } else {
        platformAlert(
          t('subscription.restorePurchases'),
          t('subscription.noSubscriptionFound'),
          [{ text: t('common.ok') }]
        );
      }
    } catch (error) {
      logger.error('Paywall', 'Restore failed', { error });
      platformAlert(
        t('subscription.error'),
        t('subscription.restoreFailed'),
        [{ text: t('common.ok') }]
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PaywallScreen
      colors={colors}
      isPro={isPro}
      selectedPlan={selectedPlan}
      onSelectPlan={setSelectedPlan}
      onSubscribe={handleSubscribe}
      onRestore={handleRestore}
      loading={loading}
      monthlyPrice={monthlyPrice}
      yearlyPrice={yearlyPrice}
    />
  );
}
